import { z } from 'zod';
import synonymeRoh from './kategorie-synonyme-skr03.json' with { type: 'json' };
import katalogRoh from './lexware-kategorien-skr03.json' with { type: 'json' };
/**
 * **Was Lexware Office selbst über seine Buchungskategorien veröffentlicht** —
 * Auftrag `LX-kontenautomatch`, Spec
 * `docs/superpowers/specs/2026-09-29-lexware-migration-design.md` Abschnitt
 * 20.20, Nachtrag `D-93`, Fall `E-2084`.
 *
 * ## Woher die Zuordnung kommt, und wie weit sie reicht
 *
 * Die Schnittstelle von Lexware Office liefert zu einer Buchungskategorie keine
 * Kontonummer (`[Q10]`). Eine vollständige Liste „Kategorie → SKR03-Konto“
 * veröffentlicht Lexware Office **nicht** (gesucht am Stand der Datei); sie steht
 * nur im Konto des Kunden selbst (Kontenübersicht mit „Kategorie anzeigen“).
 * `lexware-kategorien-skr03.json` führt deshalb nur die Paare, die die
 * öffentliche Hilfe von Lexware Office **ausdrücklich** nennt — je Eintrag mit
 * der Adresse des Hilfeartikels. Eine Kategorie, die die Hilfe mit mehreren
 * Konten nennt („Provisionsumsätze“), steht nicht darin: Ein Konto daraus zu
 * wählen wäre geraten.
 *
 * Übernommen sind nur der Name der Kategorie und die Kontonummer — keine
 * Kontenbezeichnung des Kontenrahmens (die gehört DATEV).
 *
 * ## Wie verglichen wird
 *
 * Über {@link kategorieSchluessel}: Kleinschreibung, Umlaute ausgeschrieben, jede
 * Folge anderer Zeichen als Buchstabe oder Ziffer ein Leerzeichen. „Zinsen /
 * Gebühren“ und „Zinsen/Gebühren“ sind dieselbe Kategorie; „Gehälter Minijob“ und
 * „Gehälter“ sind es nicht — der Katalog vergleicht genau, die Ähnlichkeit ist
 * eine eigene, schwächere Quelle (`apps/api/src/vorsystem/kontenvorschlag.ts`).
 *
 * ## Warum ein JSON-Import
 *
 * Aus demselben Grund wie `datenregel-vorlagen.ts`: Das API-Abbild trägt je Paket
 * nur `dist`, und `tsc` schreibt die importierte JSON-Datei dorthin. Die Form wird
 * **beim Laden** geprüft; ein Katalog mit einem Formfehler oder einer Dublette
 * lässt das Modul mit einer benannten Meldung scheitern.
 */
/** Eine Kontonummer des SKR03, wie sie der Katalog trägt. */
const kontonummer = z.string().regex(/^[0-9]{4}$/, 'Eine Kontonummer des SKR03 hat vier Ziffern.');
export const lexwareKategorieEintragSchema = z
    .object({
    kategorie: z.string().min(1).max(100),
    konto: kontonummer,
    quelle: z.string().regex(/^https:\/\/help\.lexware\.de\/\S+$/, 'Die Quelle ist ein Hilfeartikel von Lexware Office.'),
})
    .strict();
export const lexwareKategorienKatalogSchema = z
    .object({
    kontenrahmen: z.literal('SKR03'),
    stand: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    hinweis: z.string().min(1),
    eintraege: z.array(lexwareKategorieEintragSchema).min(1),
})
    .strict();
/** Der Vergleichsschlüssel eines Kategorienamens (Kleinschreibung, Umlaute ausgeschrieben, Satzzeichen zu Leerzeichen). */
export function kategorieSchluessel(name) {
    return name
        .toLocaleLowerCase('de-DE')
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
/**
 * Prüft einen Katalog: Form und Dubletten (zwei Einträge mit demselben
 * Vergleichsschlüssel). Leer heisst bestanden.
 */
export function pruefeLexwareKategorienkatalog(roh) {
    const geprueft = lexwareKategorienKatalogSchema.safeParse(roh);
    if (!geprueft.success) {
        return geprueft.error.issues.map((i) => `Form: ${i.path.join('.')}: ${i.message}`);
    }
    const befunde = [];
    const gesehen = new Set();
    for (const e of geprueft.data.eintraege) {
        const schluessel = kategorieSchluessel(e.kategorie);
        if (schluessel === '')
            befunde.push(`Leerer Vergleichsschlüssel: ${e.kategorie}`);
        if (gesehen.has(schluessel))
            befunde.push(`Dublette: ${e.kategorie}`);
        gesehen.add(schluessel);
    }
    return befunde;
}
function ladeKatalog() {
    const befunde = pruefeLexwareKategorienkatalog(katalogRoh);
    if (befunde.length > 0) {
        throw new Error(`lexware-kategorien-skr03.json verletzt seinen Vertrag: ${befunde.join('; ')}`);
    }
    return lexwareKategorienKatalogSchema.parse(katalogRoh);
}
/** Der eingecheckte Katalog, beim Laden geprüft. */
export const LEXWARE_KATEGORIEN_SKR03 = ladeKatalog();
const NACH_SCHLUESSEL = new Map(LEXWARE_KATEGORIEN_SKR03.eintraege.map((e) => [kategorieSchluessel(e.kategorie), e]));
/** Das Konto, das Lexware Office öffentlich für diese Kategorie nennt, sonst `null`. */
export function lexwareKategorieKonto(name) {
    if (name === null)
        return null;
    return NACH_SCHLUESSEL.get(kategorieSchluessel(name)) ?? null;
}
/* ------------------------------------------------------------------------ */
/* Die Synonymliste (`kategorie-synonyme-skr03.json`)                        */
/* ------------------------------------------------------------------------ */
export const kategorieSynonymEintragSchema = z
    .object({
    begriff: z.string().min(1).max(100),
    konto: kontonummer,
})
    .strict();
/**
 * Gebräuchliche Kategorienamen und das Konto des SKR03, das sie meinen — die Synonymliste der Namensgleichheit
 * (`D-93`). Anders als der Katalog oben stammt sie nicht von Lexware Office, sondern ist die Zuordnung dieses Systems;
 * ein Treffer ist deshalb eine Namensgleichheit (`quelle: name`) und nie mehr. Jedes Konto der Liste muss der
 * Kontenrahmen dieses Systems führen (geprüft gegen die Datenbank, `E-2084`).
 */
export const kategorieSynonymListeSchema = z
    .object({
    kontenrahmen: z.literal('SKR03'),
    hinweis: z.string().min(1),
    eintraege: z.array(kategorieSynonymEintragSchema).min(1),
})
    .strict();
/** Prüft die Synonymliste: Form und Dubletten. Leer heisst bestanden. */
export function pruefeKategorieSynonyme(roh) {
    const geprueft = kategorieSynonymListeSchema.safeParse(roh);
    if (!geprueft.success) {
        return geprueft.error.issues.map((i) => `Form: ${i.path.join('.')}: ${i.message}`);
    }
    const befunde = [];
    const gesehen = new Set();
    for (const e of geprueft.data.eintraege) {
        const schluessel = kategorieSchluessel(e.begriff);
        if (schluessel === '')
            befunde.push(`Leerer Vergleichsschlüssel: ${e.begriff}`);
        if (gesehen.has(schluessel))
            befunde.push(`Dublette: ${e.begriff}`);
        gesehen.add(schluessel);
    }
    return befunde;
}
function ladeSynonyme() {
    const befunde = pruefeKategorieSynonyme(synonymeRoh);
    if (befunde.length > 0) {
        throw new Error(`kategorie-synonyme-skr03.json verletzt seinen Vertrag: ${befunde.join('; ')}`);
    }
    return kategorieSynonymListeSchema.parse(synonymeRoh);
}
/** Die eingecheckte Synonymliste, beim Laden geprüft. */
export const KATEGORIE_SYNONYME_SKR03 = ladeSynonyme();
const SYNONYM_NACH_SCHLUESSEL = new Map(KATEGORIE_SYNONYME_SKR03.eintraege.map((e) => [kategorieSchluessel(e.begriff), e]));
/** Das Konto der Synonymliste für genau diesen Kategorienamen (nach {@link kategorieSchluessel}), sonst `null`. */
export function kategorieSynonymKonto(name) {
    if (name === null)
        return null;
    return SYNONYM_NACH_SCHLUESSEL.get(kategorieSchluessel(name)) ?? null;
}
//# sourceMappingURL=lexware-kategorien-skr03.js.map